import CustomModal from 'components/Common/CustomModal'
import React, { useRef, useState } from 'react'
import { Spinner } from 'reactstrap'

import { ReactComponent as Upload } from "../../assets/img/uploadfile.svg"
import api from 'api'
import { toast } from 'react-toastify'
import { getMediaUrl } from '../../config'
import VariantEditor, { validateVariants, variantsForSubmit } from './VariantEditor'

const inputStyle = {
    backgroundColor: "#F3F3F3",
    border: "none",
    borderRadius: "8px",
    fontFamily: "Outfit",
    width: "100%",
    padding: "12px 17px",
    minHeight: '44px',
};

const labelStyle = {
    marginTop: '10px',
    fontWeight: 600,
    fontSize: '16px',
    fontFamily: "Outfit",
    color: '#1C1C1C',
};

const EditProduct = ({ handleModal, brandId, getBrandProducts, selectedProduct }) => {
    const [file, setFile] = useState();
    const [removeExistingImage, setRemoveExistingImage] = useState(false);
    const [saving, setSaving] = useState(false);
    const uploadref = useRef(null)
    const [data, setData] = useState({
        name: selectedProduct?.productName ?? "",
        pointsRequired: selectedProduct?.pointsRequired ?? "",
        stockLevel: selectedProduct?.stockLevel ?? "",
        description: selectedProduct?.description ?? ""
    })
    const [hasVariants, setHasVariants] = useState(Boolean(selectedProduct?.hasVariants))
    const [variants, setVariants] = useState(
        Array.isArray(selectedProduct?.variants)
            ? selectedProduct.variants.map((v) => ({
                _id: v._id,
                name: v.name ?? '',
                stock: typeof v.stock === 'number' ? v.stock : Number(v.stock) || 0,
                sku: v.sku ?? '',
                image: v.image ?? '',
            }))
            : []
    )
    const [variantsError, setVariantsError] = useState(null)

    const existingImage = selectedProduct?.productImage
        ? getMediaUrl(selectedProduct.productImage)
        : '';

    const handleToggleHasVariants = (next) => {
        if (!next && hasVariants && variants.length > 0) {
            const ok = window.confirm(
                'Removing variants will delete the variants below. Customers will see one stock level. Continue?'
            )
            if (!ok) return
            setVariants([])
        }
        setHasVariants(next)
        setVariantsError(null)
    }

    const handleEditProduct = () => {
        if (saving) return;
        if (hasVariants) {
            const result = validateVariants(variants)
            if (!result.valid) {
                setVariantsError(result.message)
                return
            }
            setVariantsError(null)
        }

        const formdata = new FormData()
        formdata.append('productName', data.name)
        if (file) formdata.append('product_img', file)
        if (removeExistingImage && !file) formdata.append('removeProductImage', 'true')
        formdata.append('pointsRequired', data.pointsRequired)
        formdata.append('description', data.description)
        formdata.append('brandId', brandId)
        formdata.append('hasVariants', String(hasVariants))
        if (hasVariants) {
            formdata.append('variants', JSON.stringify(variantsForSubmit(variants)))
        } else {
            formdata.append('stockLevel', data.stockLevel)
        }
        setSaving(true)
        api('put', `/brandproducts/${selectedProduct?._id}`, formdata).then((res) => {
            toast.success(res.message)
            handleModal()
            getBrandProducts()
        }).catch((err) => {
            const code = err?.response?.data?.code
            const msg = err?.response?.data?.message
            if (hasVariants && code && (code.startsWith('VARIANT') || code === 'INVALID_VARIANTS')) {
                setVariantsError(msg || 'Please fix the variants below.')
            }
            console.log(err)
        }).finally(() => {
            setSaving(false)
        })
    }

    const handleChange = (e) => {
        setData((prev) => ({ ...prev, [e.target.name]: e.target.value }))
    }

    const handleFileChange = (event) => {
        if (event.target.files.length) {
            setFile(event.target.files[0]);
            setRemoveExistingImage(false);
        } else {
            setFile();
        }
    };

    // What to show in the preview area:
    //   1. Newly-picked file (blob URL) wins.
    //   2. Otherwise, existing product image — unless the user explicitly removed it.
    const previewSrc = file
        ? URL.createObjectURL(file)
        : (!removeExistingImage && existingImage ? existingImage : '');
    const showRemoveExisting = !file && existingImage && !removeExistingImage;

    const customFooter = (
        <>
            <button className='vs-btn-cancel' onClick={handleModal} disabled={saving}>
                Cancel
            </button>
            <button
                disabled={saving}
                className={`vs-btn-save ${saving ? 'disabled' : ''}`}
                onClick={handleEditProduct}
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
            >
                {saving ? (<><Spinner size='sm' /> Saving…</>) : 'Update'}
            </button>
        </>
    );

    return (
        <CustomModal
            heading='Edit Product'
            handleModal={handleModal}
            wide
            stickyFooterOnMobile
            footer={customFooter}
        >
            <input
                type="file"
                id="fileUpload"
                onChange={handleFileChange}
                style={{ display: "none" }}
                ref={uploadref}
                accept="image/*"
            />
            <label style={labelStyle}>Product Image</label><br />
            {!previewSrc ? (
                <label className='d-flex justify-content-center align-items-center' htmlFor="fileUpload" style={{
                    backgroundColor: '#F3F3F3',
                    border: '2px dashed #E4E4E4',
                    borderRadius: '16px',
                    fontFamily: "Outfit",
                    width: "100%",
                    minHeight: "140px",
                    cursor: "pointer",
                }}>
                    <div className='d-flex flex-column justify-content-center align-items-center'>
                        <Upload />
                        <span className='mt-1' style={{ fontSize: '15px', fontWeight: 500 }}>Choose photo</span>
                        <span style={{ fontSize: '12px', color: '#aaa', marginTop: '4px' }}>PNG, JPG up to ~5MB</span>
                    </div>
                </label>
            ) : (
                <div style={{ position: 'relative' }}>
                    <div
                        style={{
                            minHeight: "140px",
                            cursor: "pointer",
                            background: '#F9F9F9',
                            border: '1px solid #E4E4E4',
                            borderRadius: '16px',
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                        onClick={() => uploadref.current.click()}
                        title='Click to choose a different photo'
                    >
                        <img
                            style={{ maxHeight: '220px', maxWidth: '100%', objectFit: 'contain' }}
                            src={previewSrc}
                            alt=""
                        />
                    </div>
                    <div style={{ display: 'flex', gap: '10px', marginTop: '8px', flexWrap: 'wrap' }}>
                        <button
                            type='button'
                            onClick={() => uploadref.current.click()}
                            className='vsr-action-btn'
                            style={{ background: 'transparent', border: '1px solid #E0E0E0', color: '#362FD8', borderRadius: '8px', padding: '8px 14px', cursor: 'pointer', fontFamily: 'Outfit', fontWeight: 500, fontSize: '14px', minHeight: '40px' }}
                        >
                            Change photo
                        </button>
                        {showRemoveExisting && (
                            <button
                                type='button'
                                onClick={() => { setRemoveExistingImage(true); setFile(undefined); }}
                                className='vsr-action-btn'
                                style={{ background: 'transparent', border: '1px solid #F0C0C0', color: '#C53030', borderRadius: '8px', padding: '8px 14px', cursor: 'pointer', fontFamily: 'Outfit', fontWeight: 500, fontSize: '14px', minHeight: '40px' }}
                            >
                                Remove photo
                            </button>
                        )}
                        {file && (
                            <button
                                type='button'
                                onClick={() => setFile(undefined)}
                                className='vsr-action-btn'
                                style={{ background: 'transparent', border: '1px solid #E0E0E0', color: '#666', borderRadius: '8px', padding: '8px 14px', cursor: 'pointer', fontFamily: 'Outfit', fontWeight: 500, fontSize: '14px', minHeight: '40px' }}
                            >
                                Cancel new photo
                            </button>
                        )}
                    </div>
                </div>
            )}

            <label style={labelStyle} htmlFor="">Product Name</label><br />
            <div className=' text-right'>
                <input onChange={handleChange} name='name' value={data.name} placeholder='Enter here' style={inputStyle} type="text" maxLength={24} />
                <span style={{ color: '#828282', fontSize: '14px', fontWeight: 400, fontFamily: "Outfit" }}>{data.name.length}-24 character</span>
            </div>
            <label style={labelStyle} htmlFor="">Points Required</label><br />
            <div className=' text-right'>
                <input onChange={handleChange} name='pointsRequired' value={data.pointsRequired} placeholder='Enter here' style={inputStyle} type="text" maxLength={24} />
            </div>
            <div
                className='custom-control custom-control-alternative custom-checkbox'
                style={{ padding: 0, marginTop: '14px', marginBottom: '4px' }}
            >
                <input
                    className='checkboxInput'
                    id='editProductHasVariants'
                    type='checkbox'
                    checked={hasVariants}
                    onChange={(e) => handleToggleHasVariants(e.target.checked)}
                    style={{ marginRight: '8px' }}
                />
                <label
                    htmlFor='editProductHasVariants'
                    style={{ marginBottom: 0, fontWeight: 600, fontSize: '15px', fontFamily: 'Outfit', color: '#1C1C1C', cursor: 'pointer' }}
                >
                    This item has variants
                </label>
                <div style={{ marginTop: '4px', fontSize: '13px', color: '#828282', fontFamily: 'Outfit', paddingLeft: '24px' }}>
                    Use variants for size, color, flavor — anything where stock differs.
                </div>
            </div>

            {hasVariants ? (
                <VariantEditor variants={variants} onChange={setVariants} error={variantsError} />
            ) : (
                <>
                    <label style={labelStyle} htmlFor="">Stock Level </label><br />
                    <div className=' text-right'>
                        <input onChange={handleChange} name='stockLevel' value={data.stockLevel} placeholder='Enter here' style={inputStyle} type="text" maxLength={24} />
                    </div>
                </>
            )}
            <label style={{ ...labelStyle, marginTop: '14px' }} htmlFor="">Description</label><br />
            <div className=' text-right'>
                <textarea onChange={handleChange} name='description' value={data.description} placeholder='Enter here' maxLength={120} style={{
                    backgroundColor: '#F3F3F3',
                    border: 'none',
                    borderRadius: '8px',
                    fontFamily: "Outfit",
                    width: "100%",
                    padding: '12px 17px',
                    resize: "none",
                    minHeight: '88px',
                }} />
            </div>
        </CustomModal>
    )
}

export default EditProduct
